import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import { sortByAreaName } from '@/lib/domain/regionSurcharge';
import type {
  OverseasShippingPolicy,
  OverseasShippingPolicyPayload,
} from '../types/overseasShipping';

/**
 * ID21 해외 배송비 정책 API. (be-order 계약.) 정책+국가그룹+요율을 중첩 body 로 다룬다.
 * envelope { success, data } 를 adminFetch 로 호출한다.
 * ⚠️ 목록 GET 응답은 `data.items[]` (다른 BO 목록과 동일 패턴) — `data` 를 배열로
 * 직접 쓰면 `data.map is not a function` 으로 페이지가 크래시한다.
 */
const BASE = '/admin/v1/shipping/overseas-policies';

interface ListEnvelope {
  success: boolean;
  data?: { items?: OverseasShippingPolicy[] | null } | null;
}

interface SingleEnvelope {
  success: boolean;
  data?: OverseasShippingPolicy | null;
}

/**
 * BE 가 nested countryGroups/rates 를 null 로 줄 수도 있어, 렌더 전에 배열을 보장한다
 * (`.map` 크래시 방지). 정책 단건을 정규화한다.
 */
const normalizePolicy = (p: OverseasShippingPolicy): OverseasShippingPolicy => ({
  ...p,
  countryGroups: (p.countryGroups ?? []).map((g) => ({
    ...g,
    countryCodes: g.countryCodes ?? [],
    rates: g.rates ?? [],
    // BE 가 이미 지역명순이지만, 국내와 같은 규칙으로 한 번 더 고정한다.
    regionSurcharges: sortByAreaName(g.regionSurcharges ?? [], (r) => r.areaName),
  })),
});

export const overseasPoliciesQueryKey = () => ['admin', 'overseas-policies'] as const;
export const overseasPolicyQueryKey = (policyId: string) =>
  ['admin', 'overseas-policy', policyId] as const;

const fetchList = async (): Promise<OverseasShippingPolicy[]> => {
  const res = await adminFetch(BASE);
  if (!res.ok) throw new Error(`overseas policies list failed: ${res.status}`);
  const env = (await res.json()) as ListEnvelope;
  return (env.data?.items ?? []).map(normalizePolicy);
};

const fetchOne = async (policyId: string): Promise<OverseasShippingPolicy> => {
  const res = await adminFetch(`${BASE}/${policyId}`);
  if (!res.ok) throw new Error(`overseas policy fetch failed: ${res.status}`);
  const env = (await res.json()) as SingleEnvelope;
  if (!env.data) throw new Error('no data');
  return normalizePolicy(env.data);
};

export const useOverseasPoliciesQuery = () =>
  useQuery<OverseasShippingPolicy[]>({
    queryKey: overseasPoliciesQueryKey(),
    queryFn: fetchList,
  });

export const useOverseasPolicyQuery = (policyId: string | null) =>
  useQuery<OverseasShippingPolicy>({
    queryKey: overseasPolicyQueryKey(policyId ?? ''),
    queryFn: () => fetchOne(policyId as string),
    enabled: policyId != null,
  });

export const useOverseasPolicyMutation = () => {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: overseasPoliciesQueryKey() });

  const create = useMutation({
    mutationFn: async (payload: OverseasShippingPolicyPayload): Promise<OverseasShippingPolicy> => {
      const res = await adminFetch(BASE, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyFor('POST', BASE, payload) },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`overseas policy create failed: ${res.status}`);
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({
      policyId,
      payload,
    }: {
      policyId: string;
      payload: OverseasShippingPolicyPayload;
    }): Promise<OverseasShippingPolicy> => {
      const path = `${BASE}/${policyId}`;
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', path, payload) },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`overseas policy update failed: ${res.status}`);
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: (_, { policyId }) => {
      void qc.invalidateQueries({ queryKey: overseasPolicyQueryKey(policyId) });
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: async (policyId: string): Promise<void> => {
      const path = `${BASE}/${policyId}`;
      const res = await adminFetch(path, {
        method: 'DELETE',
        headers: { 'Idempotency-Key': idempotencyKeyFor('DELETE', path, '') },
      });
      if (!res.ok) throw new Error(`overseas policy delete failed: ${res.status}`);
    },
    onSuccess: invalidate,
  });

  return { create, update, remove };
};
